// Smart context retrieval from portfolio knowledge base
// This utility searches the knowledge base and returns relevant context based on user queries

import portfolioData from '../data/portfolioKnowledgeBase.json'

interface RelevantContext {
  contextText: string
  matchedTopics: string[]
  confidence: number
}

/**
 * Get relevant context from the knowledge base based on user query
 * Returns formatted context string to inject into AI system prompt
 */
export function getRelevantContext(userQuery: string): RelevantContext {
  const query = userQuery.toLowerCase()
  const matchedTopics: string[] = []
  let contextParts: string[] = []
  let totalMatches = 0

  // STEP 1: Check which topics are relevant based on keywords
  Object.entries(portfolioData.searchKeywords).forEach(([topic, keywords]) => {
    const matches = keywords.filter((kw) => query.includes(kw.toLowerCase()))
    if (matches.length > 0) {
      matchedTopics.push(topic)
      totalMatches += matches.length
    }
  })

  // CRITICAL: Add broad matching for common questions
  // These should ALWAYS trigger context retrieval
  const broadMatches = [
    'who is mateo',
    'tell me about mateo',
    'what does mateo do',
    'mateo cohen',
    'about mateo',
    'who are you',
    'what can you do',
    'what do you do',
  ]

  if (broadMatches.some((phrase) => query.includes(phrase))) {
    // Broad question - include EVERYTHING
    matchedTopics.push('profile', 'skills', 'experience')
    totalMatches += 5 // High confidence for broad questions
  }

  // STEP 2: Check FAQ for direct question matches
  const faqMatches = portfolioData.faq.filter((faqItem) => {
    // Check if query matches FAQ keywords
    return faqItem.keywords.some((keyword) =>
      query.includes(keyword.toLowerCase()),
    )
  })

  if (faqMatches.length > 0) {
    contextParts.push('\n=== RELEVANT FAQ ANSWERS ===')
    faqMatches.forEach((faq) => {
      contextParts.push(`Q: ${faq.q}`)
      contextParts.push(`A: ${faq.a}`)
      contextParts.push('')
    })
  }

  // STEP 3: Add relevant case studies based on matched topics
  if (matchedTopics.includes('ai')) {
    contextParts.push('\n=== AI EXPERIENCE (DETAILED) ===')
    contextParts.push(
      JSON.stringify(portfolioData.caseStudies.aiComparison, null, 2),
    )
    contextParts.push(
      JSON.stringify(portfolioData.caseStudies.caseWestern, null, 2),
    )
  }

  if (matchedTopics.includes('finance')) {
    contextParts.push('\n=== FINANCIAL SERVICES EXPERIENCE (DETAILED) ===')
    contextParts.push(JSON.stringify(portfolioData.caseStudies.usBank, null, 2))
  }

  if (matchedTopics.includes('security')) {
    contextParts.push('\n=== CYBERSECURITY EXPERIENCE (DETAILED) ===')
    contextParts.push(
      JSON.stringify(portfolioData.caseStudies.cyberSecurity, null, 2),
    )
  }

  // STEP 4: Add profile info for personal/availability questions
  if (
    matchedTopics.includes('personal') ||
    matchedTopics.includes('availability') ||
    matchedTopics.includes('location')
  ) {
    contextParts.push('\n=== PERSONAL & AVAILABILITY INFO ===')
    contextParts.push(JSON.stringify(portfolioData.profile, null, 2))
  }

  // STEP 5: Add tools/skills for technical questions
  if (matchedTopics.includes('tools') || matchedTopics.includes('design')) {
    contextParts.push('\n=== TOOLS & SKILLS ===')
    contextParts.push(JSON.stringify(portfolioData.tools, null, 2))
    contextParts.push(JSON.stringify(portfolioData.coreSkills, null, 2))
  }

  // STEP 6: Add achievements for metrics/results questions
  if (
    query.includes('achievement') ||
    query.includes('result') ||
    query.includes('impact') ||
    query.includes('metric')
  ) {
    contextParts.push('\n=== KEY ACHIEVEMENTS & METRICS ===')
    contextParts.push(JSON.stringify(portfolioData.achievements, null, 2))
  }

  // STEP 7: Add testimonials for reference/recommendation questions
  if (
    query.includes('testimonial') ||
    query.includes('reference') ||
    query.includes('recommendation') ||
    query.includes('what do people say')
  ) {
    contextParts.push('\n=== TESTIMONIALS ===')
    contextParts.push(JSON.stringify(portfolioData.testimonials, null, 2))
  }

  // STEP 8: Always include basic profile at the top
  const baseContext = `
=== MATEO'S PROFILE (ALWAYS REFERENCE THIS) ===
Name: ${portfolioData.profile.name}
Title: ${portfolioData.profile.title}
Experience: ${portfolioData.profile.yearsOfExperience} years
Current Role: ${portfolioData.profile.currentRole}
Location: ${portfolioData.profile.locations.primary} (${portfolioData.profile.locations.workLocation})
Availability: ${portfolioData.profile.availability.status}
Bio: ${portfolioData.profile.bioSummary}
`

  // Combine all context parts
  const fullContext = baseContext + contextParts.join('\n')

  // Calculate confidence score (0-1)
  const confidence = Math.min(1, totalMatches / 3)

  return {
    contextText: fullContext,
    matchedTopics,
    confidence,
  }
}

/**
 * Search FAQ directly for exact question matches
 * Returns the answer if found, null otherwise
 */
export function searchFAQDirect(userQuery: string): string | null {
  const query = userQuery.toLowerCase()

  // Find FAQ items where keywords match
  const matches = portfolioData.faq.filter((faqItem) =>
    faqItem.keywords.some((keyword) => query.includes(keyword.toLowerCase())),
  )

  if (matches.length > 0) {
    // Return the first (best) match
    return matches[0].a
  }

  return null
}

/**
 * Get all FAQ questions and answers as formatted text
 * Useful for including comprehensive FAQ in system prompt
 */
export function getAllFAQContext(): string {
  let faqText = '\n=== COMPLETE FAQ KNOWLEDGE BASE ===\n'

  portfolioData.faq.forEach((faqItem, index) => {
    faqText += `\n${index + 1}. Q: ${faqItem.q}\n`
    faqText += `   A: ${faqItem.a}\n`
  })

  return faqText
}
